import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FormBuilderService } from './form-builder.service';
import { RegistroUsuarioService } from './registro-usuario.service';
import { CardModule } from 'primeng/card';
import { IFormSearchLupa } from '@/shared/components/general/lupas/interfaces/lupas.interfaces';
import { SelectModule } from 'primeng/select';
import {
  IRegistroUsuario,
  IRegistroUsuarioForm,
} from '../../interfaces/consulta-externa-pqrs.interface';
import { FooterModalService } from '@/shared/components/general/footer-modal/footer-modal.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { IResponseApiGeneric } from '@/interface/general/IResponseApi';
import { finalize } from 'rxjs';
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-registro-usuario',
  standalone: true,
  imports: [
    // Angular modules
    CommonModule,
    ReactiveFormsModule,
    // PrimeNG modules
    DatePickerModule,
    InputNumberModule,
    FieldsetModule,
    ButtonModule,
    FileUploadModule,
    SelectModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    CardModule,
    InputTextModule,
    PanelModule,
  ],
  templateUrl: './registro-usuario.component.html',
  styleUrl: './registro-usuario.component.scss',
})
export class RegistroUsuarioComponent {
  buildForm!: FormGroup<IRegistroUsuarioForm>;
  initialService = inject(RegistroUsuarioService);
  footerService = inject(FooterModalService);
  formService = inject(FormBuilderService);
  dialogService = inject(DialogService);
  dialogRef = inject(DynamicDialogRef);
  loading = false;

  constructor(private messageService: MessageService) {
    this.initialService.loadInitialData();
    this.buildForm = this.formService.buildForm();
  }

  private idFormulario = 'form_PQR';
  CodGlobalLupa: IFormSearchLupa = {
    idFormulario: this.idFormulario,
    campo: 'CodGlobal',
    label: 'Empresa',
    required: true,
  };

  public labels = {
    PrimerNombre: 'Primer Nombre',
    SegundoNombre: 'Segundo Nombre',
    PrimerApellido: 'Primer Apellido',
    SegundoApellido: 'Segundo Apellido',
    RazonSocial: 'Razón Social',
  };

  get isDarkMode(): boolean {
    return document.documentElement.classList.contains('dark');
  }
  onTipoDocumentoChange(code: string) {
    const tipo = this.initialService.TipoDocumentoOptions.find(
      (t) => t.code === code,
    );
    if (!tipo) return;
    const esNIT = tipo.name === 'Número Identificación Tributaria';
    const camposPersona = [
      'PrimerNombre',
      'SegundoNombre',
      'PrimerApellido',
      'SegundoApellido',
    ];

    if (esNIT) {
      this.labels.PrimerNombre = 'Primer Nombre Representante Legal';
      this.labels.SegundoNombre = 'Segundo Nombre Representante Legal';
      this.labels.PrimerApellido = 'Primer Apellido Representante Legal';
      this.labels.SegundoApellido = 'Segundo Apellido Representante Legal';
      this.labels.RazonSocial = 'Razón Social (Empresa)';

      this.buildForm.get('RazonSocial')?.enable();
      this.buildForm.get('RazonSocial')?.setValidators([Validators.required]);
      this.buildForm.get('RazonSocial')?.updateValueAndValidity();
    } else {
      this.labels.PrimerNombre = 'Primer Nombre';
      this.labels.SegundoNombre = 'Segundo Nombre';
      this.labels.PrimerApellido = 'Primer Apellido';
      this.labels.SegundoApellido = 'Segundo Apellido';
      this.labels.RazonSocial = 'Razón Social';
      camposPersona.forEach((campo) => {
        this.buildForm.get(campo)?.enable();
        this.buildForm.get(campo)?.updateValueAndValidity();
      });
      this.buildForm.get('RazonSocial')?.reset();
      this.buildForm.get('RazonSocial')?.disable();
      this.buildForm.get('RazonSocial')?.clearValidators();
      this.buildForm.get('RazonSocial')?.updateValueAndValidity();
    }
  }

  onGuardar(): void {
    if (this.buildForm.invalid) {
      this.buildForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const v = this.buildForm.value;

    const payload: IRegistroUsuario = {
      CodTipoDocumento: v.CodTipoDocumento ?? null,
      NumeroDocumento: v.NumeroDocumento ?? null,
      RazonSocial: v.RazonSocial ?? null,
      PrimerNombre: v.PrimerNombre ?? null,
      SegundoNombre: v.SegundoNombre ?? null,
      PrimerApellido: v.PrimerApellido ?? null,
      SegundoApellido: v.SegundoApellido ?? null,
      Email: v.Email ?? null,
      Celular: v.Celular ?? null,
      Direccion: v.Direccion ?? null,
      CodGenero: v.CodGenero ?? null,
      CodGlobal: v.CodGlobal ?? null,
      codUserUpdate: v.codUserUpdate ?? '',
    };

    this.initialService
      .postCreate(payload)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response: IResponseApiGeneric<string>) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Registro creado exitosamente',
            detail: response?.mensaje || 'Creación exitosa!',
            life: 3000,
          });

          // Cerrar el modal y pasar datos al padre (opcional)
          // obj enviado al padre: { recargar: true, data: response }
          try {
            this.dialogRef.close({ recargar: true, data: response });
          } catch (e) {
            // si no existe dialogRef, ignora
          }
        },
      });
  }

  onCancelar(): void {
    try {
      this.dialogRef.close({ recargar: false });
    } catch (e) {
      // fallback si no tienes DynamicDialogRef
      console.warn('dialogRef no disponible para cerrar el modal', e);
    }
  }
}
